import { ReactElement } from 'react';
import { useRouter } from 'next/compat/router';
import Link from 'next/link';
import { getConfig as nextConfig } from '@belong/configs/next/config';
import RouteConfig from '@belong/configs/routes';
import { IOptionalStringMap, removeObjectEmptyProps } from '@belong/type-utils';
import { ILinkRenderer, LinkRendererFunction } from '@belong/types';
import { isAnchor, isExternalUrl, isKnownRoute, matchRoute, withBasePath, withoutBasePath } from '@belong/url-utils';
import { compileLinkProps, makeHrefFromLinkProps } from './utils';

// BUTTON - No HREF provided
export const button: ILinkRenderer = {
  name: 'button',
  test({ href }): boolean {
    return !href;
  },
  render(Component, props, _): ReactElement {
    const { href, asUrl, query, ...otherProps } = props;
    return <Component {...otherProps} />;
  }
};

/**
 * INTERNAL - matches known app routes
 * Formats url and params and renders an internal link using the NextJS Link component.
 * https://nextjs.org/docs/api-reference/next/link
 *
 * Features:
 * - populates tokens (:id) in destination url using values from the provided query
 * - populates tokens (:id) in destination url using values from current url if not found in the query
 * - removes any tokens used to create url from the query
 * - replaces known tokens in url params. Currently only 'page' - e.g. my-path?from={{page}}
 * - combines any passed query with any query on the asUrl and then adds them back to the url together
 * - removes the basePath from the path for links served by this app
 * */

// eslint-disable-next-line react/display-name
const SameOriginLink: LinkRendererFunction = (Component, props, config) => {
  const { href = '', query, csr = false, ...otherProps } = props;
  const currentPath = useRouter()?.asPath ?? '';
  const { basePath } = nextConfig().publicRuntimeConfig;
  const dictionary = { page: currentPath.split('/').pop() };
  const matchedRoute = matchRoute(currentPath, config.routes ?? []);
  const urlParams: IOptionalStringMap = {
    ...(matchedRoute?.match ?? {}),
    ...removeObjectEmptyProps(query)
  };

  const linkProps = compileLinkProps({
    href,
    currentPath,
    dictionary,
    urlParams,
    query
  });

  const canSPATransitionToNextURL = isKnownRoute(config.routes, href);

  // clean up href if necessary
  if (canSPATransitionToNextURL) {
    if (linkProps.href?.pathname) {
      linkProps.href.pathname = withoutBasePath(linkProps.href.pathname, basePath);
    }
  }

  /**
   * If the know the URL is served by this app, use the Next.js Link component.
   * It will create a nice SPA transition.
   *
   * Links to URLs that are not served by this app (e.g. myaccount urls from the public app)
   * cannot use the Next.js Link component. Instead we need to use a standard anchor tag.
   *
   * @see https://nextjs.org/learn/basics/navigate-between-pages/client-side
   */
  // destinationHref is needed when rendering an anchor tag
  const destinationHref = makeHrefFromLinkProps(linkProps);

  if (canSPATransitionToNextURL && !csr) {
    // hard refresh for same app route which need to add base path back
    return (
      <Component as="a" {...otherProps} href={withBasePath(destinationHref)}>
        {otherProps.children}
      </Component>
    );
  }

  /**
   * TODO: Refactor to remove legacyBehavior requirement
   * Reference: https://nextjs.org/docs/15/pages/api-reference/components/link#legacybehavior
   */
  if (canSPATransitionToNextURL && csr) {
    // url without basepath will be used when client side routing
    return (
      <Link {...linkProps} passHref legacyBehavior>
        <Component as="a" {...otherProps} />
      </Link>
    );
  }

  return (
    <Component as="a" {...otherProps} href={destinationHref}>
      {otherProps.children}
    </Component>
  );
};

// special links like mailto:xxx, tel:xxx, sms:xxxx
export const specialLink: ILinkRenderer = {
  name: 'specialLink',
  test({ href }): boolean {
    return /^(mailto|tel|sms|geo):/.test(href ?? '');
  },
  render(Component, props): ReactElement {
    const { href, asUrl, query, ...otherProps } = props;

    return <Component as="a" href={asUrl ?? href} {...otherProps} />;
  }
};

// EXTERNAL - url is fully qualified
export const externalLink: ILinkRenderer = {
  name: 'external',
  test({ href }): boolean {
    return isExternalUrl(href);
  },
  render(Component, props): ReactElement {
    const { href, asUrl, query, ...otherProps } = props;

    return <Component as="a" href={href} {...otherProps} />;
  }
};

export const sameOriginLink: ILinkRenderer = {
  name: 'internal',
  test({ href }): boolean {
    return isAnchor(href) || !isExternalUrl(href);
  },
  render: SameOriginLink
};

export const defaultRenderer = button;

export const config = new RouteConfig();
config.setLinkRenderers(button, externalLink, specialLink, sameOriginLink);
config.defaultLinkRenderer = defaultRenderer;
